import { useCallback, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import {
  EventType,
  type EventMessage,
  type AuthenticationResult,
  type PopupRequest,
  type RedirectRequest,
  type SilentRequest,
  type EndSessionRequest,
  type AccountInfo,
  type AuthError
} from "@azure/msal-browser";
import { config } from "@/config/environment";
import { msalDebugUtils } from "@/utilities/msal-debug-utils";
import { MsalService } from "@/components/security/msal-service";

/**
 * MSAL event handler hook (MSAL 5.x compatible)
 * Handles authentication events for debugging and active account sync.
 * See: https://learn.microsoft.com/en-us/entra/msal/javascript/browser/events
 */
export function useMsalEvents() {
  const { instance } = useMsal();
  const msalService = useMemo(() => MsalService.getInstance(instance), [instance]);

  const handleMsalEvent = useCallback(
    (event: EventMessage) => {
      if (!config.debug.enabled) return;

      switch (event.eventType) {
        case EventType.INITIALIZE_START: {
          msalDebugUtils.logMsalEvent("INITIALIZE_START");
          break;
        }
        case EventType.INITIALIZE_END: {
          msalDebugUtils.logMsalEvent("INITIALIZE_END");
          break;
        }
        case EventType.ACQUIRE_TOKEN_START: {
          const request = event.payload as
            | SilentRequest
            | PopupRequest
            | RedirectRequest;
          if (request?.scopes) {
            msalDebugUtils.logTokenAcquisition(request.scopes, request.account);
          } else {
            msalDebugUtils.logMsalEvent("ACQUIRE_TOKEN_START");
          }
          break;
        }
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          const tokenResult = event.payload as AuthenticationResult;
          if (tokenResult?.account) {
            msalService.setActiveAccount(tokenResult.account);
          }

          msalDebugUtils.logMsalEvent("ACQUIRE_TOKEN_SUCCESS", {
            scopes: tokenResult?.scopes,
            expiresOn: tokenResult?.expiresOn,
            accessTokenLength: tokenResult?.accessToken?.length,
            account: tokenResult?.account?.username,
            authority: tokenResult?.authority
          });

          // Log token expiry information
          if (tokenResult?.expiresOn) {
            const now = Date.now();
            const expiresAt = tokenResult.expiresOn.getTime();
            const timeUntilExpiry = expiresAt - now;

            console.log(
              "⏰ Token expires in:",
              Math.round(timeUntilExpiry / 1000),
              "seconds"
            );
            if (timeUntilExpiry < 300000) {
              console.warn("⚠️ Token expires soon (less than 5 minutes)");
            }
          }
          break;
        }
        case EventType.ACQUIRE_TOKEN_FAILURE: {
          const error = (event.error ?? event.payload) as AuthError;
          msalDebugUtils.logMsalEvent("ACQUIRE_TOKEN_FAILURE", {
            errorCode: error?.errorCode,
            errorMessage: error?.errorMessage
          });
          break;
        }
        case EventType.ACQUIRE_TOKEN_NETWORK_START: {
          msalDebugUtils.logMsalEvent("ACQUIRE_TOKEN_NETWORK_START");
          break;
        }
        case EventType.LOGIN_SUCCESS: {
          // MSAL 5.x: LOGIN_SUCCESS payload is AccountInfo (not AuthenticationResult)
          const account = event.payload as AccountInfo;
          if (account) {
            msalService.setActiveAccount(account);
          }

          msalDebugUtils.logMsalEvent("LOGIN_SUCCESS", {
            account: account?.username
          });
          break;
        }
        case EventType.LOGOUT_START: {
          const request = event.payload as EndSessionRequest;
          msalDebugUtils.logMsalEvent("LOGOUT_START", {
            account: request?.account?.username
          });
          break;
        }
        case EventType.LOGOUT_SUCCESS: {
          msalDebugUtils.logMsalEvent("LOGOUT_SUCCESS");
          break;
        }
        case EventType.LOGOUT_END: {
          msalDebugUtils.logMsalEvent("LOGOUT_END");
          break;
        }
        case EventType.LOGOUT_FAILURE: {
          const error = (event.error ?? event.payload) as AuthError;
          msalDebugUtils.logMsalEvent("LOGOUT_FAILURE", {
            errorCode: error?.errorCode,
            errorMessage: error?.errorMessage
          });
          break;
        }
        case EventType.HANDLE_REDIRECT_START: {
          msalDebugUtils.logMsalEvent("HANDLE_REDIRECT_START");
          break;
        }
        case EventType.HANDLE_REDIRECT_END: {
          msalDebugUtils.logMsalEvent("HANDLE_REDIRECT_END");
          break;
        }
        case EventType.ACTIVE_ACCOUNT_CHANGED: {
          const activeAccount = instance.getActiveAccount();
          msalDebugUtils.logMsalEvent("ACTIVE_ACCOUNT_CHANGED", {
            account: activeAccount?.username
          });
          break;
        }
        case EventType.POPUP_OPENED: {
          msalDebugUtils.logMsalEvent("POPUP_OPENED");
          break;
        }
        case EventType.RESTORE_FROM_BFCACHE: {
          msalDebugUtils.logMsalEvent("RESTORE_FROM_BFCACHE");
          break;
        }
        case EventType.BROKERED_REQUEST_START:
        case EventType.BROKERED_REQUEST_SUCCESS:
        case EventType.BROKERED_REQUEST_FAILURE:
        case EventType.BROKER_CONNECTION_ESTABLISHED: {
          msalDebugUtils.logMsalEvent(String(event.eventType));
          break;
        }
        default: {
          if (config.debug.enabled) {
            console.log(
              "Unhandled MSAL event:",
              event.eventType,
              event.payload
            );
          }
          break;
        }
      }
    },
    [msalService, instance]
  );

  useEffect(() => {
    // Register event callback
    const callbackId = instance.addEventCallback(handleMsalEvent);

    // Cleanup on unmount
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [handleMsalEvent, instance]);
}
